import { Box, SxProps, Theme } from "@mui/material";
import React, { useMemo } from "react";

export const getFormattedJsonReport = (report: unknown): string => {
  if (report == null) {
    return "";
  }

  if (typeof report === "string") {
    const trimmedReport = report.trim();
    if (!trimmedReport) {
      return "";
    }

    try {
      return JSON.stringify(JSON.parse(trimmedReport), null, 2);
    } catch {
      return report;
    }
  }

  try {
    return JSON.stringify(report, null, 2);
  } catch {
    return String(report);
  }
};

export const hasJsonReport = (report: unknown): boolean => {
  if (report == null) {
    return false;
  }

  return typeof report === "string" ? report.trim().length > 0 : true;
};

type JsonTokenType = "plain" | "key" | "string" | "number" | "boolean" | "null";

interface JsonToken {
  text: string;
  type: JsonTokenType;
}

const jsonTokenPattern =
  /("(?:\\u[\da-fA-F]{4}|\\[^u]|[^\\"])*"|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?|true|false|null)/g;

const tokenizeJson = (json: string): JsonToken[] => {
  const tokens: JsonToken[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = jsonTokenPattern.exec(json)) != null) {
    const [text] = match;

    if (match.index > lastIndex) {
      tokens.push({ text: json.slice(lastIndex, match.index), type: "plain" });
    }

    const textAfterToken = json.slice(match.index + text.length);
    let type: JsonTokenType = "number";

    if (text.startsWith('"')) {
      type = /^\s*:/.test(textAfterToken) ? "key" : "string";
    } else if (text === "true" || text === "false") {
      type = "boolean";
    } else if (text === "null") {
      type = "null";
    }

    tokens.push({ text, type });
    lastIndex = match.index + text.length;
  }

  if (lastIndex < json.length) {
    tokens.push({ text: json.slice(lastIndex), type: "plain" });
  }

  return tokens;
};

const tokenColorByType: Record<Exclude<JsonTokenType, "plain">, string> = {
  key: "#7b341e",
  string: "#0f766e",
  number: "#1d4ed8",
  boolean: "#b45309",
  null: "#6b7280",
};

interface JsonReportViewerProps {
  report: unknown;
  maxHeight?: number | string;
  sx?: SxProps<Theme>;
}

const JsonReportViewer: React.FC<JsonReportViewerProps> = ({ report, maxHeight = 520, sx }) => {
  const formattedReport = useMemo(() => getFormattedJsonReport(report), [report]);
  const tokens = useMemo(() => tokenizeJson(formattedReport), [formattedReport]);

  return (
    <Box
      component="pre"
      sx={{
        bgcolor: "grey.50",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        color: "grey.900",
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        fontSize: 13,
        lineHeight: 1.6,
        m: 0,
        maxHeight,
        overflow: "auto",
        p: 2,
        whiteSpace: "pre",
        ...sx,
      }}
    >
      {tokens.map((token, index) =>
        token.type === "plain" ? (
          <React.Fragment key={`${token.type}-${index}`}>{token.text}</React.Fragment>
        ) : (
          <Box
            component="span"
            key={`${token.type}-${index}`}
            sx={{ color: tokenColorByType[token.type] }}
          >
            {token.text}
          </Box>
        ),
      )}
    </Box>
  );
};

export default JsonReportViewer;
